import { baseApi } from './baseApi';
import {
  ExpenseDto,
  ExpenseCategoryDto,
  ExpensePaymentMethodDto,
  CreateExpensePaymentMethodRequest,
  UpdateExpensePaymentMethodRequest,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  CancelExpenseRequest,
  ExpenseFilter,
  ExpensesPage,
  ExpenseAttachmentDto,
  ExpenseCaptureResultDto,
  AutocapturaNoticeDto,
} from '../types/expense';

export const expensesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------------------------------------------------------------------
    // Expenses
    // ------------------------------------------------------------------
    getExpenses: builder.query<ExpensesPage, { restaurantId: string; filter: ExpenseFilter }>({
      query: ({ restaurantId, filter }) => ({
        url: `/restaurants/${restaurantId}/expenses`,
        params: filter,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'Expense' as const, id })),
              { type: 'Expense' as const, id: 'LIST' },
            ]
          : [{ type: 'Expense' as const, id: 'LIST' }],
    }),

    getExpense: builder.query<ExpenseDto, { restaurantId: string; id: string }>({
      query: ({ restaurantId, id }) => `/restaurants/${restaurantId}/expenses/${id}`,
      providesTags: (_result, _error, { id }) => [{ type: 'Expense' as const, id }],
    }),

    createExpense: builder.mutation<ExpenseDto, { restaurantId: string; data: CreateExpenseRequest }>({
      query: ({ restaurantId, data }) => ({
        url: `/restaurants/${restaurantId}/expenses`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'Expense' as const, id: 'LIST' },
        { type: 'PnL' as const, id: 'CURRENT' },
        // Creating with PaidFromCash impacts the CashRegister + CashMovements aggregates.
        { type: 'CashRegister' as const, id: 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'ALL_OPEN' },
        { type: 'CashMovement' as const, id: 'LIST' },
      ],
    }),

    updateExpense: builder.mutation<
      ExpenseDto,
      { restaurantId: string; id: string; data: UpdateExpenseRequest }
    >({
      query: ({ restaurantId, id, data }) => ({
        url: `/restaurants/${restaurantId}/expenses/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Expense' as const, id },
        { type: 'Expense' as const, id: 'LIST' },
        // PaymentMethod edits flow through this mutation and affect the P&L breakdown.
        { type: 'PnL' as const, id: 'CURRENT' },
      ],
    }),

    cancelExpense: builder.mutation<
      void,
      { restaurantId: string; id: string; data: CancelExpenseRequest }
    >({
      query: ({ restaurantId, id, data }) => ({
        url: `/restaurants/${restaurantId}/expenses/${id}/cancel`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Expense' as const, id },
        { type: 'Expense' as const, id: 'LIST' },
        { type: 'PnL' as const, id: 'CURRENT' },
        // Cancellation may create a CORRECTION reversal CashMovement.
        { type: 'CashRegister' as const, id: 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'ALL_OPEN' },
        { type: 'CashMovement' as const, id: 'LIST' },
      ],
    }),

    // ------------------------------------------------------------------
    // Expense Categories
    // ------------------------------------------------------------------
    getExpenseCategories: builder.query<ExpenseCategoryDto[], { restaurantId: string }>({
      query: ({ restaurantId }) => `/restaurants/${restaurantId}/expense-categories`,
      providesTags: [{ type: 'ExpenseCategory' as const, id: 'LIST' }],
    }),

    // ------------------------------------------------------------------
    // Expense Payment Methods (catálogo separado de Ventas)
    // ------------------------------------------------------------------
    // includeInactive=true is used by the QuickAddPaymentMethodModal to dedup against
    // soft-deleted entries client-side (a user typing "Bancomer" must collide with an
    // inactive "bancomer" — otherwise the backend 409 surfaces as a race).
    getExpensePaymentMethods: builder.query<
      ExpensePaymentMethodDto[],
      { restaurantId: string; includeInactive?: boolean }
    >({
      query: ({ restaurantId, includeInactive }) => ({
        url: `/restaurants/${restaurantId}/expense-payment-methods`,
        params: includeInactive ? { includeInactive: true } : undefined,
      }),
      // Different cache buckets for active-only vs all so neither evicts the other when
      // the modal opens. Tag invalidation hits both buckets on create/update/delete.
      providesTags: (_result, _error, { includeInactive }) => [
        { type: 'ExpensePaymentMethod' as const, id: includeInactive ? 'LIST_ALL' : 'LIST_ACTIVE' },
      ],
    }),

    createExpensePaymentMethod: builder.mutation<
      ExpensePaymentMethodDto,
      { restaurantId: string; data: CreateExpensePaymentMethodRequest }
    >({
      query: ({ restaurantId, data }) => ({
        url: `/restaurants/${restaurantId}/expense-payment-methods`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ExpensePaymentMethod' as const, id: 'LIST_ACTIVE' },
        { type: 'ExpensePaymentMethod' as const, id: 'LIST_ALL' },
      ],
    }),

    updateExpensePaymentMethod: builder.mutation<
      ExpensePaymentMethodDto,
      { restaurantId: string; id: string; data: UpdateExpensePaymentMethodRequest }
    >({
      query: ({ restaurantId, id, data }) => ({
        url: `/restaurants/${restaurantId}/expense-payment-methods/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ExpensePaymentMethod' as const, id: 'LIST_ACTIVE' },
        { type: 'ExpensePaymentMethod' as const, id: 'LIST_ALL' },
        // Renaming a method changes the denormalized PaymentMethodName on every related
        // Expense DTO, so list views must refetch.
        { type: 'Expense' as const, id: 'LIST' },
        { type: 'PnL' as const, id: 'CURRENT' },
      ],
    }),

    deleteExpensePaymentMethod: builder.mutation<void, { restaurantId: string; id: string }>({
      query: ({ restaurantId, id }) => ({
        url: `/restaurants/${restaurantId}/expense-payment-methods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'ExpensePaymentMethod' as const, id: 'LIST_ACTIVE' },
        { type: 'ExpensePaymentMethod' as const, id: 'LIST_ALL' },
      ],
    }),

    // ------------------------------------------------------------------
    // Comprobantes (adjuntos)
    // ------------------------------------------------------------------
    getExpenseAttachments: builder.query<
      ExpenseAttachmentDto[],
      { restaurantId: string; expenseId: string }
    >({
      query: ({ restaurantId, expenseId }) =>
        `/restaurants/${restaurantId}/expenses/${expenseId}/attachments`,
      providesTags: (_result, _error, { expenseId }) => [
        { type: 'ExpenseAttachment' as const, id: expenseId },
      ],
    }),

    /**
     * Descarga el contenido de un comprobante.
     *
     * Devuelve un OBJECT URL (string), no un Blob. Dos razones, ambas concretas:
     *
     * 1. El store tiene `serializableCheck` activo con sólo 'persist/PERSIST'
     *    ignorado (store/index.ts). Un Blob en el cache de RTK Query lo dispara.
     * 2. Un `<img src>` necesita una URL, no un Blob — y no puede apuntar al
     *    endpoint directo porque el JWT viaja en header y el browser no lo manda
     *    al cargar una imagen.
     *
     * El responseHandler ramifica sobre `response.ok` a propósito: fetchBaseQuery
     * lo ejecuta ANTES de validateStatus, así que un `response.blob()` a secas
     * también convertiría los errores. El JSON de un 401 se volvería un Blob y el
     * chequeo `errorData?.code === 'SESSION_EXPIRED'` de baseApi leería `.code`
     * sobre un Blob → undefined → el manejo de sesión invalidada dejaría de
     * funcionar en este endpoint.
     */
    getExpenseAttachmentContent: builder.query<
      string,
      { restaurantId: string; expenseId: string; attachmentId: string }
    >({
      query: ({ restaurantId, expenseId, attachmentId }) => ({
        url: `/restaurants/${restaurantId}/expenses/${expenseId}/attachments/${attachmentId}/content`,
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            // text() UNA vez y luego JSON.parse. Un response.json().catch(() =>
            // response.text()) intentaría leer el body dos veces: si el JSON falla,
            // el stream ya se consumió y el text() del catch lanza, perdiendo justo
            // el mensaje de error que se quería rescatar.
            const raw = await response.text();
            try {
              return JSON.parse(raw);
            } catch {
              return raw;
            }
          }
          return URL.createObjectURL(await response.blob());
        },
      }),
      // La revocación va atada al ciclo de vida del cache y NO al montaje del
      // componente: dos miniaturas del mismo adjunto comparten la entrada, y
      // revocar en el unmount de una dejaría a la otra con una URL muerta.
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved }) {
        let url: string | undefined;
        try {
          const { data } = await cacheDataLoaded;
          url = data;
          await cacheEntryRemoved;
        } catch {
          // cacheDataLoaded rechaza si la query falló — no hay URL que revocar.
        } finally {
          if (url) URL.revokeObjectURL(url);
        }
      },
      providesTags: (_result, _error, { attachmentId }) => [
        { type: 'ExpenseAttachment' as const, id: attachmentId },
      ],
    }),

    uploadExpenseAttachment: builder.mutation<
      ExpenseAttachmentDto,
      { restaurantId: string; expenseId: string; file: File }
    >({
      query: ({ restaurantId, expenseId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/restaurants/${restaurantId}/expenses/${expenseId}/attachments`,
          method: 'POST',
          body: formData,
          // Sin Content-Type a mano: el browser tiene que poner el boundary del
          // multipart. fetchBaseQuery detecta FormData y no lo pisa.
        };
      },
      invalidatesTags: (_result, _error, { expenseId }) => [
        { type: 'ExpenseAttachment' as const, id: expenseId },
        // Refresca attachmentCount en la tabla y la card de "sin comprobante".
        { type: 'Expense' as const, id: 'LIST' },
        { type: 'Expense' as const, id: expenseId },
      ],
    }),

    deleteExpenseAttachment: builder.mutation<
      void,
      { restaurantId: string; expenseId: string; attachmentId: string }
    >({
      query: ({ restaurantId, expenseId, attachmentId }) => ({
        url: `/restaurants/${restaurantId}/expenses/${expenseId}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { expenseId }) => [
        { type: 'ExpenseAttachment' as const, id: expenseId },
        { type: 'Expense' as const, id: 'LIST' },
        { type: 'Expense' as const, id: expenseId },
      ],
    }),

    // ------------------------------------------------------------------
    // Autocaptura
    // ------------------------------------------------------------------

    /**
     * Manda la foto y devuelve lo que el sistema logró leer.
     *
     * El ÚNICO campo del form-data es `file`. No existe `qrContent`: el QR se decodifica
     * en el servidor a partir de estos mismos bytes, que es lo que hace que el archivo
     * archivado y sus datos sean el mismo documento y no dos cosas que se prometieron
     * iguales.
     *
     * `analysisStatus` puede venir `unreadable` o `unavailable` y **eso no es un error**:
     * son 200 con captureId, el archivo ya quedó guardado y el usuario captura a mano.
     */
    analyzeExpenseCapture: builder.mutation<
      ExpenseCaptureResultDto,
      { restaurantId: string; file: File | Blob; fileName: string }
    >({
      query: ({ restaurantId, file, fileName }) => {
        const formData = new FormData();
        formData.append('file', file, fileName);
        return {
          url: `/restaurants/${restaurantId}/expense-capture/analyze`,
          method: 'POST',
          body: formData,
        };
      },
    }),

    getAutocapturaNotice: builder.query<AutocapturaNoticeDto, { restaurantId: string }>({
      query: ({ restaurantId }) => `/restaurants/${restaurantId}/expense-capture/notice`,
      providesTags: [{ type: 'AutocapturaNotice' as const, id: 'ME' }],
    }),

    markAutocapturaNoticeSeen: builder.mutation<void, { restaurantId: string }>({
      query: ({ restaurantId }) => ({
        url: `/restaurants/${restaurantId}/expense-capture/notice/seen`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'AutocapturaNotice' as const, id: 'ME' }],
    }),
  }),
});

export const {
  useGetExpensesQuery,
  useLazyGetExpensesQuery,
  useGetExpenseQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useCancelExpenseMutation,
  useGetExpenseCategoriesQuery,
  useGetExpensePaymentMethodsQuery,
  useCreateExpensePaymentMethodMutation,
  useUpdateExpensePaymentMethodMutation,
  useDeleteExpensePaymentMethodMutation,
  useGetExpenseAttachmentsQuery,
  useGetExpenseAttachmentContentQuery,
  useUploadExpenseAttachmentMutation,
  useDeleteExpenseAttachmentMutation,
  useAnalyzeExpenseCaptureMutation,
  useGetAutocapturaNoticeQuery,
  useMarkAutocapturaNoticeSeenMutation,
} = expensesApi;
